import React from 'react'
import { cx, focusRing } from '../../lib/utils'

interface TabsContextType {
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabsContext = React.createContext<TabsContextType | undefined>(undefined)

function useTabs() {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider')
  }
  return context
}

interface TabsProps {
  defaultValue: string
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  className?: string
}

function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue)

  const activeTab = value !== undefined ? value : internalValue
  const setActiveTab = React.useCallback(
    (tab: string) => {
      if (value === undefined) {
        setInternalValue(tab)
      }
      onValueChange?.(tab)
    },
    [value, onValueChange]
  )

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  )
}

interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

function TabsList({ className, children, ...props }: TabsListProps) {
  return (
    <div
      role="tablist"
      className={cx(
        // Scrolls inside itself rather than pushing the page sideways: six
        // tabs do not fit a phone, and a horizontally scrolling page is a
        // worse answer than a horizontally scrolling strip.
        'flex gap-5 overflow-x-auto border-b border-gray-200 [scrollbar-width:none] dark:border-gray-800',
        '[&::-webkit-scrollbar]:hidden',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string
  children: React.ReactNode
}

function Tab({ value, className, children, ...props }: TabProps) {
  const { activeTab, setActiveTab } = useTabs()
  const isActive = activeTab === value

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      onClick={() => setActiveTab(value)}
      className={cx(
        'relative shrink-0 whitespace-nowrap pb-3 text-sm transition-colors',
        isActive
          ? 'font-semibold text-gray-950 dark:text-white'
          : 'font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200',
        focusRing,
        className
      )}
      {...props}
    >
      {children}
      {/* The mark is the brand's blue while the label goes black: the colour
          points, the weight says which one you are reading. */}
      {isActive && (
        <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-500" />
      )}
    </button>
  )
}

interface TabsPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  children: React.ReactNode
}

function TabsPanel({ value, className, children, ...props }: TabsPanelProps) {
  const { activeTab } = useTabs()

  if (activeTab !== value) {
    return null
  }

  return (
    <div role="tabpanel" className={cx('mt-6', className)} {...props}>
      {children}
    </div>
  )
}

export { Tabs, TabsList, Tab, TabsPanel }
